import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner, { CardSkeleton, PageLoader } from '../components/LoadingSpinner';
import SalesDetailsModal from '../components/SalesDetailsModal';
import PaymentModal from '../components/PaymentModal';

import {
  Users, Package, ShoppingCart, TrendingUp, FileText, DollarSign,
  AlertTriangle, Calendar, Clock, CheckCircle, XCircle,
  UserCheck, Building, Briefcase, Target, Bell, AlertCircle,
  ExternalLink, RefreshCw, Phone, Package2, CreditCard
} from 'lucide-react';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  // Sales Details Modal State
  const [salesModalConfig, setSalesModalConfig] = useState({ isOpen: false, type: null, records: [], title: '', loading: false });

  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleSalesKpiClick = async (type) => {
    let title = '';
    if (type === 'today') title = "Today's Sales";
    else if (type === 'month') title = "This Month's Sales";
    else if (type === 'all') title = "Total Sales";

    setSalesModalConfig({ isOpen: true, type, title, records: [], loading: true });

    try {
      const response = await axios.get(`/admin/sales-details?period=${type}`);
      setSalesModalConfig(prev => ({ ...prev, records: response.data, loading: false }));
    } catch (error) {
      console.error('Failed to fetch sales details:', error);
      toast.error('Failed to load sales details');
      setSalesModalConfig(prev => ({ ...prev, loading: false }));
    }
  };

  const handlePaymentSubmit = async (paymentUpdateData) => {
    if (!paymentUpdateData.amount_received || parseFloat(paymentUpdateData.amount_received) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      const endpoint = selectedRecord.documentType === 'invoice'
        ? `/sales/invoices/${selectedRecord.id}/payment`
        : `/sales/closed-orders/${selectedRecord.id}/payment`;

      console.log(`Processing payment for ${selectedRecord.documentType} at ${endpoint}`);

      // Ensure amount is parsed as a number
      const amountReceived = parseFloat(paymentUpdateData.amount_received);

      await axios.put(endpoint, {
        payment_type: paymentUpdateData.payment_type,
        amount_received: amountReceived,
        notes: paymentUpdateData.notes
      });

      toast.success('Payment updated successfully');
      setShowPaymentModal(false);
      setSelectedRecord(null);

      // Refresh dashboard main metrics
      fetchDashboardData(0, true);

      // Re-fetch sales details to show updated status/amount in the open SalesDetailsModal
      if (salesModalConfig.isOpen) {
        const response = await axios.get(`/admin/sales-details?period=${salesModalConfig.type}`);
        setSalesModalConfig(prev => ({ ...prev, records: response.data }));
      }
    } catch (error) {
      console.error('Payment update failed:', error);
      toast.error(error.response?.data?.error || 'Failed to update payment');
    }
  };

  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchDashboardData();
    }
  }, [dataFetched]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing dashboard data...');
      fetchDashboardData(0, true); // Silent refresh
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async (retryCount = 0, silentRefresh = false) => {
    try {
      if (!silentRefresh) setLoading(true);

      const response = await axios.get('/admin/dashboard');

      // Ensure category_sales has data for the pie chart
      const dashboardDataWithDefaults = {
        ...response.data,
        category_sales: response.data.category_sales && response.data.category_sales.length > 0
          ? response.data.category_sales
          : [
            { name: 'Furniture', value: 0, sales: 0 },
            { name: 'Lighting', value: 0, sales: 0 },
            { name: 'Wallpaper', value: 0, sales: 0 },
            { name: 'Flooring', value: 0, sales: 0 }
          ]
      };

      setDashboardData(dashboardDataWithDefaults);
      setLastUpdated(new Date());

      console.log('📊 Dashboard data updated:', {
        todaySales: response.data.total_sales_today || 0,
        monthlySales: response.data.total_sales_month || 0,
        overallSales: response.data.total_sales_overall || 0,
        notifications: response.data.notifications?.length || 0,
        lowStock: response.data.low_stock_alerts || 0,
        lowStockProducts: response.data.low_stock_products?.length || 0,
        categorySales: response.data.category_sales?.length || 0,
        pendingPayments: response.data.pending_payments || 0,
        timestamp: new Date().toLocaleTimeString()
      });
      console.log('💰 Admin Dashboard - Today Sales:', response.data.total_sales_today);
      console.log('💰 Admin Dashboard - Monthly Sales:', response.data.total_sales_month);
      console.log('📦 Low Stock Products:', response.data.low_stock_products);
      console.log('📊 Category Sales Data:', response.data.category_sales);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);

      // Handle 429 (Too Many Requests) with retry logic
      if (error.response?.status === 429 && retryCount < 1) {
        console.log(`Rate limited, retrying in 1 second...`);
        setTimeout(() => {
          fetchDashboardData(retryCount + 1);
        }, 1000);
        return;
      }

      // Show error toast instead of dummy data
      toast.error('Failed to load dashboard data. Please try again.');

      // Set empty data to prevent UI crash but show 0/empty state
      setDashboardData({
        total_sales_today: 0,
        total_sales_month: 0,
        total_sales_overall: 0,
        pending_payments: 0,
        pending_payments_amount: 0,
        low_stock_alerts: 0,
        zero_stock_count: 0,
        total_products: 0,
        total_invoices: 0,
        total_customers: 0,
        total_employees: 0,
        notifications: [],
        low_stock_products: [],
        pending_payment_invoices: [],
        monthly_sales: [],
        category_sales: [],
        recent_activities: []
      });
      setLoading(false);


    } finally {
      setLoading(false);
    }
  };



  if (loading && !dashboardData) {
    return <PageLoader text="Loading dashboard data..." />;
  }

  return (
    <div className="py-4 sm:py-6 bg-[#F6F8F5] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Complete overview of your interior design business</p>
            {lastUpdated && (
              <p className="text-sm text-gray-500 mt-1">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>


        {/* Quick Action Cards for Critical Issues */}
        {(dashboardData?.zero_stock_count > 0 || dashboardData?.pending_payments > 0) && (
          <div className="mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">🎯 Quick Actions Required</h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {dashboardData?.zero_stock_count > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-red-900">Out of Stock Products</h3>
                      <p className="text-sm text-red-700">{dashboardData.zero_stock_count} products need immediate restocking</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {dashboardData.low_stock_products?.filter(p => p.currentStock === 0).slice(0, 3).map((product) => (
                      <div key={product.id} className="flex justify-between items-center bg-white p-3 rounded border">
                        <div>
                          <p className="font-medium text-slate-900">{product.name}</p>
                          <p className="text-sm text-gray-600">{product.category} • SKU: {product.sku}</p>
                        </div>
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                          0 units
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="mt-4 w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                    onClick={() => navigate('/admin/products')}
                  >
                    Manage Inventory →
                  </button>
                </div>
              )}

              {dashboardData?.pending_payments > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-900">Pending Payments</h3>
                      <p className="text-sm text-yellow-700">{dashboardData.pending_payments} invoices awaiting payment</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {dashboardData.pending_payment_invoices?.slice(0, 3).map((invoice) => (
                      <div key={invoice.id} className="flex justify-between items-center bg-white p-3 rounded border">
                        <div>
                          <p className="font-medium text-slate-900">{invoice.customerName}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(invoice.invoiceDate).toLocaleDateString()} • {invoice.salesperson}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">₹{invoice.pendingAmount?.toLocaleString()}</p>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${invoice.paymentStatus === 'pending'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {invoice.paymentStatus}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    className="mt-4 w-full bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 transition-colors"
                    onClick={() => navigate('/admin/payments?status=pending,overdue,partial')}
                  >
                    View All Pending Payments →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sales Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div 
            onClick={() => handleSalesKpiClick('today')}
            className="card cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all ring-1 ring-transparent hover:ring-[#0A5F38]/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Sales</p>
                <p className="text-3xl font-bold text-[#0A5F38]">₹{(dashboardData?.total_sales_today || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="p-3 bg-[#C5A57D] rounded-xl">
                <Calendar className="h-6 w-6 text-[#0A5F38]" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => handleSalesKpiClick('month')}
            className="card cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all ring-1 ring-transparent hover:ring-[#0A5F38]/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-3xl font-bold text-[#0A5F38]">₹{(dashboardData?.total_sales_month || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="p-3 bg-[#C5A57D] rounded-xl">
                <TrendingUp className="h-6 w-6 text-[#0A5F38]" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => handleSalesKpiClick('all')}
            className="card cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all ring-1 ring-transparent hover:ring-[#0A5F38]/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-3xl font-bold text-[#0A5F38]">₹{(dashboardData?.total_sales_overall || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="p-3 bg-[#C5A57D] rounded-xl">
                <DollarSign className="h-6 w-6 text-[#0A5F38]" />
              </div>
            </div>
          </div>
        </div>

        {/* Business Metrics - Compact 4-column layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card text-center p-4">
            <div className="p-2.5 bg-[#C5A57D] rounded-full mx-auto mb-2 w-fit">
              <Package className="h-5 w-5 text-[#0A5F38]" />
            </div>
            <p className="text-xs text-gray-600">Products</p>
            <p className="text-2xl font-bold text-[#0A5F38]">{dashboardData?.total_products || 0}</p>
          </div>

          <div className="card text-center p-4">
            <div className="p-2.5 bg-[#C5A57D] rounded-full mx-auto mb-2 w-fit">
              <FileText className="h-5 w-5 text-[#0A5F38]" />
            </div>
            <p className="text-xs text-gray-600">Invoices</p>
            <p className="text-2xl font-bold text-[#0A5F38]">{dashboardData?.total_invoices || 0}</p>
          </div>

          <div className="card text-center p-4">
            <div className="p-2.5 bg-[#C5A57D] rounded-full mx-auto mb-2 w-fit">
              <Users className="h-5 w-5 text-[#0A5F38]" />
            </div>
            <p className="text-xs text-gray-600">Customers</p>
            <p className="text-2xl font-bold text-[#0A5F38]">{dashboardData?.total_customers || 0}</p>
          </div>

          <div className="card text-center p-4">
            <div className="p-2.5 bg-[#C5A57D] rounded-full mx-auto mb-2 w-fit">
              <UserCheck className="h-5 w-5 text-[#0A5F38]" />
            </div>
            <p className="text-xs text-gray-600">Employees</p>
            <p className="text-2xl font-bold text-[#0A5F38]">{dashboardData?.total_employees || 0}</p>
          </div>
        </div>

        {/* Critical Alerts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div
            onClick={() => navigate('/admin/payments?status=pending,overdue,partial')}
            className={`card text-center p-4 cursor-pointer transition-all hover:shadow-lg ${dashboardData?.pending_payments > 0 ? 'ring-2 ring-[#C5A57D]/30 bg-amber-50' : ''}`}
          >
            <div className="p-2.5 bg-[#C5A57D] rounded-full mx-auto mb-2 w-fit">
              <Clock className="h-5 w-5 text-[#0A5F38]" />
            </div>
            <p className="text-xs text-gray-600">Pending Payments</p>
            <p className="text-2xl font-bold text-[#0A5F38]">₹{(dashboardData?.pending_payments_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            {dashboardData?.pending_payments > 0 && (
              <div className="mt-1">
                <span className="px-2 py-1 bg-[#C5A57D]/20 text-[#0A5F38] rounded-full text-xs font-medium">
                  {dashboardData.pending_payments} Invoices
                </span>
              </div>
            )}
          </div>

          <div className={`card text-center p-4 ${dashboardData?.low_stock_alerts > 0 ? 'ring-2 ring-[#C5A57D]/30 bg-red-50' : ''}`}>
            <div className="p-2.5 bg-[#C5A57D] rounded-full mx-auto mb-2 w-fit">
              <AlertTriangle className="h-5 w-5 text-[#0A5F38]" />
            </div>
            <p className="text-xs text-gray-600">Low Stock Alerts</p>
            <p className="text-2xl font-bold text-[#0A5F38]">{dashboardData?.low_stock_alerts || 0}</p>
            {dashboardData?.zero_stock_count > 0 && (
              <div className="mt-1">
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                  {dashboardData.zero_stock_count} Critical
                </span>
              </div>
            )}
          </div>
        </div>




      </div>

      <SalesDetailsModal
        isOpen={salesModalConfig.isOpen}
        onClose={() => setSalesModalConfig(prev => ({ ...prev, isOpen: false }))}
        title={salesModalConfig.title}
        records={salesModalConfig.records}
        type={salesModalConfig.type}
        loading={salesModalConfig.loading}
        onUpdatePayment={(record) => {
          // Normalize record object shape depending on whether it's closed order or invoice
          const isClosedOrder = record.status === 'closed_order';
          const fullRecord = record.fullRecord || record;
          
          setSelectedRecord({
            ...record,
            id: fullRecord._id || record.id || record._id,
            documentType: isClosedOrder ? 'closedOrder' : 'invoice',
            documentNumber: isClosedOrder 
              ? (fullRecord.closed_order_number || fullRecord.order_number || record.documentNumber)
              : (fullRecord.invoice_number || fullRecord.invoiceNumber || record.documentNumber),
            customerName: fullRecord.customerId?.name || fullRecord.customer_name || record.customerName || 'Walk-in Customer',
            fullRecord: fullRecord
          });
          setShowPaymentModal(true);
        }}
      />

      {showPaymentModal && selectedRecord && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedRecord(null);
          }}
          record={selectedRecord.fullRecord || selectedRecord}
          onPaymentUpdate={handlePaymentSubmit}
          paymentHistory={selectedRecord.fullRecord?.payments || selectedRecord.payments || []}
        />
      )}
    </div>
  );
};

export default AdminDashboard;


